import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import {
  BackgroundImageWrapper,
  Image,
  Grid,
  H,
  P,
  Wrapper,
} from '@farewill/ui'
import { screenMax } from '@farewill/ui/helpers/responsive'
import { BORDER_RADIUS, BREAKPOINT, COLOR, GTR } from '@farewill/ui/tokens'
import { Link } from '@reach/router'
import ArticleAuthorDetails from 'components/ArticleAuthorDetails'
import AspectRatioWrapper from 'components/AspectRatioWrapper'
import { ARTICLE_TYPES } from 'lib/contentful/constants'

const SCROLL_VIEW_BREAKPOINT = BREAKPOINT.S + 100
const CARD_MIN_WIDTH = '270px'

const StyledP = styled(P)`
  ${screenMax.l`
    font-size: 18px;
  `}
`

const StyledArticleAuthorDetails = styled.div`
  color: ${COLOR.BLACK};

  & > div {
    margin-bottom: ${GTR.M};
  }
`

const StyledAspectRatioWrapper = styled(AspectRatioWrapper)`
  margin-bottom: ${GTR.L};
  border-radius: ${BORDER_RADIUS.S};
  overflow: hidden;
`

const StyledGridScrollContainer = styled(Wrapper)`
  @media (max-width: ${SCROLL_VIEW_BREAKPOINT}px) {
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    margin-left: -${GTR.L};
    margin-right: -${GTR.L};
    padding-left: ${GTR.L};
  }
`

const StyledGrid = styled(Grid)`
  @media (max-width: ${SCROLL_VIEW_BREAKPOINT}px) {
    width: calc(3 * (${CARD_MIN_WIDTH} + ${GTR.S}) + ${GTR.L});
    width: clamp(
      calc(3 * (${CARD_MIN_WIDTH} + ${GTR.S}) + ${GTR.L}),
      230vw,
      1300px
    );
    padding-right: ${GTR.L};
  }
`
const StyledGridCard = styled(Grid.Item)`
  @media (max-width: ${SCROLL_VIEW_BREAKPOINT}px) {
    scroll-snap-align: center;
    width: 70vw;
    width: clamp(${CARD_MIN_WIDTH}, 70vw, 400px);
  }

  width: calc(100% - 8px);
  padding-bottom: ${GTR.XL};
`

const StyledTexture = styled.div`
  position: absolute;
  top: -160px;
  right: 0;

  ${screenMax.m`
    display: none;
  `}
`

const StyledWrapper = styled.div`
  position: relative;
`

const TitleWithLink = ({ className, linkedArticle, children }) =>
  linkedArticle ? (
    <Link
      className={className}
      to={`${linkedArticle.type === ARTICLE_TYPES.BLOG ? 'blog' : 'articles'}/${
        linkedArticle.slug
      }`}
    >
      {children}
    </Link>
  ) : (
    children
  )

const StyledTitleWithLink = styled(TitleWithLink)`
  text-decoration: none;
`

const Tips = ({ tipsList }) => (
  <StyledWrapper>
    <StyledTexture>
      <Image path="textures/blob-2-yellow" width={180} widthFromXL={240} />
    </StyledTexture>
    <Grid container margin={['L', 'auto']}>
      <Grid.Item
        spanFromM={10}
        startColumnFromM={2}
        spanFromL={8}
        startColumnFromL="auto"
      >
        <H tag="h2" size="L" decorative>
          Planning your own reception or memorial
        </H>
      </Grid.Item>
      <Grid.Item
        spanFromM={10}
        startColumnFromM={2}
        spanFromL={8}
        startColumnFromL="auto"
        margin={[0, 0, 'L']}
      >
        <StyledP size="L" color={COLOR.BLACK}>
          Instead of a funeral service, a memorial can give you the chance to
          come together with friends and family and celebrate your loved one’s
          life. If this is something you’re considering, these ideas from our
          team could help you get started.
        </StyledP>
      </Grid.Item>
      <Grid.Item>
        <StyledGridScrollContainer>
          <StyledGrid gapFromL="XL" columns={3}>
            {tipsList?.map(
              ({
                id,
                image,
                teamMember,
                linkedArticle,
                title,
                description,
              }) => (
                <StyledGridCard key={id} span={1}>
                  <StyledAspectRatioWrapper ratio={4 / 3}>
                    <BackgroundImageWrapper
                      imagePath={image?.file?.url}
                      formatImagePath={({ imagePath, width }) =>
                        `${imagePath}?w=${width}`
                      }
                      imageWidth={600}
                      alt={image?.alt}
                      cover
                    />
                  </StyledAspectRatioWrapper>
                  <StyledArticleAuthorDetails>
                    <ArticleAuthorDetails
                      name={teamMember?.name?.split(' ')[0]}
                      title={teamMember?.title}
                      image={teamMember?.image}
                      avatarSize="large"
                    />
                  </StyledArticleAuthorDetails>
                  <StyledTitleWithLink linkedArticle={linkedArticle}>
                    <H tag="h3" size="S">
                      {title}
                    </H>
                  </StyledTitleWithLink>
                  <P color={COLOR.BLACK}>{description?.description}</P>
                </StyledGridCard>
              )
            )}
          </StyledGrid>
        </StyledGridScrollContainer>
      </Grid.Item>
    </Grid>
  </StyledWrapper>
)

Tips.propTypes = {
  tipsList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      image: PropTypes.shape({
        file: PropTypes.shape({
          url: PropTypes.string,
          details: PropTypes.shape({
            image: PropTypes.shape({
              height: PropTypes.number,
              width: PropTypes.number,
            }),
          }),
        }),
      }),
      title: PropTypes.string,
      description: PropTypes.shape({
        description: PropTypes.string,
      }),
      linkedArticle: PropTypes.shape({
        slug: PropTypes.string,
        type: PropTypes.string,
      }),
      teamMember: PropTypes.shape({
        name: PropTypes.string,
        slug: PropTypes.string,
        title: PropTypes.string,
        image: PropTypes.shape({
          file: PropTypes.shape({
            details: PropTypes.shape({
              image: PropTypes.shape({
                width: PropTypes.number,
                height: PropTypes.number,
              }),
            }),
            url: PropTypes.string,
          }),
        }),
      }),
    })
  ).isRequired,
}

TitleWithLink.propTypes = {
  className: PropTypes.string.isRequired,
  linkedArticle: PropTypes.shape({
    type: PropTypes.string,
    slug: PropTypes.string,
  }).isRequired,
  children: PropTypes.node.isRequired,
}

export default Tips
